import React, { useState } from 'react';
import { Moment } from 'moment-timezone';

import PrimaryButton from '@modules/common/components/buttons/PrimaryButton';
import { alertError, alertWarn, alertSuccess } from '@src/lib/alert';
import SentryUtils from '@src/lib/sentry';
import SecondaryButton from '@modules/common/components/buttons/SecondaryButton';
import { styled } from '@modules/common/components/ThemeProvider/utils';
import FormInputField from '@modules/common/components/Field/FormInputField';
import { ModalContent } from '@modules/common/components/BaseModal/ModalContent';
import { ModalActions } from '@modules/common/components/BaseModal/ModalActions';
import Header from '@modules/common/components/Header';
import { ModalHeader } from '@modules/common/components/BaseModal/ModalHeader';
import BaseForm from '@modules/common/components/BaseForm';

import {
  OAuthFlow,
  OAuthClientDescriptor,
  UpdateOAuthClientDescriptorPayload,
} from '../../../components/types/oauth-client';
import ClientResource from '../../../components/services/ClientResource';
import ClientForm from '../ClientForm';

type Props = {
  client: OAuthClientDescriptor;
  onClientUpdated: () => void;
  handleClose: () => void;
};

function EditClientForm({ client, onClientUpdated, handleClose }: Props) {
  const [selectedOAuthFlow, setSelectedOAuthFlow] = useState<OAuthFlow>(
    (client.parameters.supportedFlows && client.parameters.supportedFlows[0]) ||
      'CLIENT_CREDENTIALS',
  );
  const [clientName, setClientName] = useState<string>(client.name);
  const [expiryDate, setExpiryDate] = useState<Moment | undefined>(client.expiryDate);
  const [allowedOriginDomains, setAllowedOriginDomains] = useState<string[] | undefined>(
    client.parameters.allowedWebOrigins,
  );
  const [updatingClient, setUpdatingClient] = useState<boolean>(false);

  async function updateClient(handleClose: () => void) {
    const clientDescriptor: UpdateOAuthClientDescriptorPayload = {
      ...client,
      name: clientName,
      expiryDate,
      parameters: {
        ...client.parameters,
        allowedWebOrigins: allowedOriginDomains,
      },
    };
    try {
      setUpdatingClient(true);
      await ClientResource.update({
        ...clientDescriptor,
        id: client.clientId,
      });
      alertSuccess('Client successfully updated');
      onClientUpdated();
      handleClose();
    } catch (err) {
      const errorMessage = 'Something went wrong while creating OAuth client';
      alertError(errorMessage);
      SentryUtils.captureAPIError(err, errorMessage);
    }
    setUpdatingClient(false);
  }

  function onSubmitForm(handleClose: () => void) {
    if (!clientName) {
      alertWarn('Client name must be set.');
      return;
    }
    if (!selectedOAuthFlow) {
      alertWarn('Supported OAuth flow must be set.');
      return;
    }
    updateClient(handleClose);
  }

  return (
    <>
      <ModalHeader>
        <Header content="Update existing OAuth Client" />
      </ModalHeader>
      <ModalContent>
        <BaseForm>
          <StyledInputField
            label="Client ID:"
            fieldName="id"
            value={client.clientId}
            fieldLabel="Client ID:"
            readOnly
          />
        </BaseForm>
        <ClientForm
          isLoading={updatingClient}
          selectedOAuthFlow={selectedOAuthFlow}
          setSelectedOAuthFlow={setSelectedOAuthFlow}
          clientName={clientName}
          setClientName={setClientName}
          expiryDate={expiryDate}
          setExpiryDate={setExpiryDate}
          isSetupForSPA={client.isSPAEnabled}
          allowedOriginDomains={allowedOriginDomains}
          setAllowedOriginDomains={setAllowedOriginDomains}
          expirationDisabled={!client.expiryDate}
          expiryDateDisabled
          supportedFlowDisabled
          isEditingSPASettings
        />
      </ModalContent>
      <ModalActions>
        <SecondaryButton icon="remove" onClick={handleClose} content="Close" />
        <PrimaryButton
          content="Update"
          onClick={() => onSubmitForm(handleClose)}
          loading={updatingClient}
          icon="save"
        />
      </ModalActions>
    </>
  );
}

export default EditClientForm;

const StyledInputField = styled(FormInputField)`
  margin-bottom: 1em !important;
  input {
    opacity: 0.45;
  }
`;
